/**
 * Utilities for formatting DPE/VMT send errors returned from the backend.
 *
 * Backend shape (error responses):
 *  - { message: 'VMT send failed.', error: <dpePayload|string>, gmResponses, failedSegment }
 *
 * DPE payload example:
 *  {
 *    totalRecords: 1,
 *    message: 'Product selection data processed failed',
 *    recordsProcessedSuccessful: 0,
 *    recordsProcessedFailed: 1,
 *    validationErrorRecords: [],
 *    processedErrorRecords: [{ ..., errorMessage: 'Dealer_price is more than product price in VMT', ... }]
 *  }
 */

const normalizeWhitespace = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

const prettifyDpeMessage = (value) => {
  const raw = normalizeWhitespace(value);
  if (!raw) return '';
  // Common DPE format: "Dealer_price ..." -> "Dealer price ..."
  const noUnderscores = raw.replace(/_/g, ' ');
  return normalizeWhitespace(noUnderscores);
};

const truncateMiddle = (value, head = 6, tail = 4) => {
  const raw = normalizeWhitespace(value);
  if (!raw) return '';
  if (raw.length <= head + tail + 3) return raw;
  return `${raw.slice(0, head)}…${raw.slice(-tail)}`;
};

const recordDescriptor = (record) => {
  if (!record || typeof record !== 'object') return '';
  const parts = [];
  if (record.bac) parts.push(`BAC ${record.bac}`);
  if (record.productCd) parts.push(`Product ${record.productCd}`);
  if (record.referenceId) parts.push(`Ref ${truncateMiddle(record.referenceId)}`);
  if (parts.length === 0) return '';
  return `(${parts.join(', ')})`;
};

const extractDpePayload = (axiosError) => {
  const data = axiosError?.response?.data;
  if (!data) return null;
  // Prefer the backend-proxy'd DPE object at `data.error`.
  if (data && typeof data === 'object' && data.error) return data.error;
  // Sometimes callers may pass the raw DPE payload directly.
  return data;
};

/**
 * Returns a concise, user-facing error message for snackbar/toasts.
 */
export const formatVmtSendErrorMessage = (axiosError, options = {}) => {
  const { maxRecords = 2 } = options;
  const status = axiosError?.response?.status;
  const data = axiosError?.response?.data;
  const dpe = extractDpePayload(axiosError);

  const processedErrors = Array.isArray(dpe?.processedErrorRecords) ? dpe.processedErrorRecords : [];
  const validationErrors = Array.isArray(dpe?.validationErrorRecords) ? dpe.validationErrorRecords : [];

  const candidateRecords =
    processedErrors.length > 0
      ? processedErrors
      : validationErrors.length > 0
        ? validationErrors
        : [];

  if (candidateRecords.length > 0) {
    const summaries = candidateRecords.slice(0, Math.max(1, maxRecords)).map((record) => {
      const desc = recordDescriptor(record);
      const msg = prettifyDpeMessage(record?.errorMessage || record?.message || '');
      if (desc && msg) return `${desc} ${msg}`;
      return msg || desc || 'Record rejected.';
    });
    const remaining = Math.max(candidateRecords.length - summaries.length, 0);
    const suffix = remaining > 0 ? ` (+${remaining} more)` : '';
    return `DPE rejected ${candidateRecords.length} record(s): ${summaries.join('; ')}${suffix}`;
  }

  const topLevelDpeMessage = prettifyDpeMessage(dpe?.message);
  if (topLevelDpeMessage && status === 400) {
    return `DPE rejected submission: ${topLevelDpeMessage}`;
  }

  // Fall back to backend message/error string, then axios message.
  const backendErrorString =
    typeof data?.error === 'string' ? normalizeWhitespace(data.error) : '';
  const backendMessage = normalizeWhitespace(data?.message || '');
  const axiosMessage = normalizeWhitespace(axiosError?.message || '');

  if (backendErrorString) return backendErrorString;
  if (backendMessage) return backendMessage;
  return axiosMessage || 'Submission failed.';
};


